/**
 * Surveillance Page - Enhanced Monitoring Dashboard
 * ==================================================
 * Premium real-time transaction monitoring with modern design.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Sidebar } from '../includes/sidebar';
import {
    Eye,
    RefreshCw,
    Download,
    Filter,
    TrendingUp,
    TrendingDown,
    DollarSign,
    Activity,
    Users,
    AlertTriangle,
    Zap,
    Clock,
    FileSpreadsheet,
    Loader2,
    Search,
    ArrowUpRight,
    ArrowDownRight,
    BarChart3,
    PieChart,
    Sparkles,
    Radio,
    History
} from 'lucide-react';
import {
    LineChart,
    Line,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart as RechartsPieChart,
    Pie,
    Cell,
    Legend
} from 'recharts';
import { MonitoringFilters, monitoringApi, Agent } from '../../api/monitoring.api';

// ============================================
// TYPE DEFINITIONS
// ============================================
interface SubTransaction {
    id: string;
    amount: number;
    time: string;
}

interface TransactionItem {
    id: string;
    agent: string;
    type: string;
    client: string;
    account?: string;
    count: number;
    amount: number;
    time: string;
    ago?: string;
    subTransactions?: SubTransaction[];
}

interface Alert {
    type: string;
    message: string;
    level: string;
    triggered_at: string;
    count?: number;
    amount?: number;
}

interface SurveillanceStats {
    by_type: Record<string, {
        count: number;
        amount: number;
        percentage: number;
    }>;
    total_depots: { amount: number; count: number };
    total_retraits: { amount: number; count: number };
    balance_globale: { montant_net: number; total_transactions: number };
    alerts: {
        high_activity?: Alert;
        high_amount?: Alert;
    };
    daily_trend: Array<{
        date: string;
        name: string;
        count: number;
        amount: number;
    }>;
    hourly_distribution: Array<{
        hour: number;
        count: number;
    }>;
    total_amount: number;
    total_commission: number;
    avg_amount: number;
}

const EMPTY_LINE_DATA: any[] = [];

// Line chart mock data - last 7 days

// Pie chart data - based on stats.by_type
const PIE_COLORS = ['#10b981', '#3b82f6', '#ef4444', '#a855f7'];

// Period options
const PERIOD_OPTIONS = [
    { value: 'today', label: "Aujourd'hui" },
    { value: 'yesterday', label: "Hier" },
    { value: 'week', label: "Cette semaine" },
    { value: 'month', label: "Ce mois" },
    { value: 'quarter', label: "Ce trimestre" },
    { value: 'custom', label: "Personnalisé" },
];

// Type display config
const TYPE_CONFIG: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
    'depo_sol': { label: 'Depo SOL', color: 'text-emerald-700', bgColor: 'bg-emerald-500', icon: <TrendingDown className="h-3 w-3" /> },
    'depo_bs': { label: 'Depo Ti Kanè', color: 'text-blue-700', bgColor: 'bg-blue-500', icon: <TrendingDown className="h-3 w-3" /> },
    'retrait_bs': { label: 'Retrè Ti Kanè', color: 'text-red-700', bgColor: 'bg-red-500', icon: <TrendingUp className="h-3 w-3" /> },
    'retrait_sol': { label: 'Retrè SOL', color: 'text-orange-700', bgColor: 'bg-orange-500', icon: <TrendingUp className="h-3 w-3" /> },
    'depo_solide': { label: 'Dépo Solidè', color: 'text-purple-700', bgColor: 'bg-purple-500', icon: <Sparkles className="h-3 w-3" /> },
    'transfer': { label: 'Transfè', color: 'text-slate-700', bgColor: 'bg-slate-500', icon: <ArrowUpRight className="h-3 w-3" /> },
};

// Format currency - always full decimal with thousands separator
const formatCurrency = (amount: number, _short = false) => {
    // Always use full number format with 2 decimal places
    const formatted = new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
    return `${formatted} HTG`;
};

// Format number with spaces
const formatNumber = (num: number) => {
    return new Intl.NumberFormat('fr-FR').format(num);
};

// Animated counter component
const AnimatedCounter = ({ value, prefix = '', suffix = '' }: { value: number; prefix?: string; suffix?: string }) => {
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        const duration = 1000;
        const steps = 30;
        const increment = value / steps;
        let current = 0;

        const timer = setInterval(() => {
            current += increment;
            if (current >= value) {
                setDisplayValue(value);
                clearInterval(timer);
            } else {
                setDisplayValue(Math.floor(current));
            }
        }, duration / steps);

        return () => clearInterval(timer);
    }, [value]);

    return <span>{prefix}{formatNumber(displayValue)}{suffix}</span>;
};

export default function SurveillancePage() {
    const navigate = useNavigate();
    const { toast } = useToast();

    // State
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [liveIndicator, setLiveIndicator] = useState(true);

    // Filters
    const [period, setPeriod] = useState<MonitoringFilters['period']>('today');
    const [selectedAgent, setSelectedAgent] = useState<string>('');
    const [agentSearchQuery, setAgentSearchQuery] = useState('');
    const [showAgentDropdown, setShowAgentDropdown] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Agents from API
    const [agents, setAgents] = useState<Agent[]>([]);
    const [loadingAgents, setLoadingAgents] = useState(false);

    // Filtered agents based on search (use real agents)
    const filteredAgents = agents.filter(agent =>
        agent.code.toLowerCase().includes(agentSearchQuery.toLowerCase()) ||
        agent.name.toLowerCase().includes(agentSearchQuery.toLowerCase())
    );

    // Data
    const [stats, setStats] = useState<SurveillanceStats>({
        by_type: {
            depo_sol: { count: 0, amount: 0, percentage: 0 },
            depo_bs: { count: 0, amount: 0, percentage: 0 },
            retrait_bs: { count: 0, amount: 0, percentage: 0 },
            retrait_sol: { count: 0, amount: 0, percentage: 0 },
            depo_solide: { count: 0, amount: 0, percentage: 0 },
            transfer: { count: 0, amount: 0, percentage: 0 },
        },
        total_depots: { amount: 0, count: 0 },
        total_retraits: { amount: 0, count: 0 },
        balance_globale: { montant_net: 0, total_transactions: 0 },
        alerts: {},
        daily_trend: [],
        hourly_distribution: [],
        total_amount: 0,
        total_commission: 0,
        avg_amount: 0
    });
    const [realtimeData, setRealtimeData] = useState<TransactionItem[]>([]);
    const [recentData, setRecentData] = useState<TransactionItem[]>([]);
    const [lastUpdate, setLastUpdate] = useState(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));

    // Transaction detail modal
    const [selectedTransaction, setSelectedTransaction] = useState<TransactionItem | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Open transaction details
    const openTransactionDetails = (transaction: TransactionItem) => {
        setSelectedTransaction(transaction);
        setShowDetailModal(true);
    };

    // Live indicator animation
    useEffect(() => {
        const interval = setInterval(() => {
            setLiveIndicator(prev => !prev);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Fetch agents from API on mount
    useEffect(() => {
        const fetchAgents = async () => {
            setLoadingAgents(true);
            try {
                const response = await monitoringApi.getAgents();
                setAgents(response.agents);
            } catch (error) {
                console.error('Failed to fetch agents:', error);
                toast({ title: 'Erè', description: 'Pa kapab chaje lis ajan yo', type: 'error' });
            } finally {
                setLoadingAgents(false);
            }
        };
        fetchAgents();
    }, []);

    // Clear all filters
    const clearFilters = () => {
        setPeriod('today');
        setSelectedAgent('');
        setAgentSearchQuery('');
        setStartDate('');
        setEndDate('');
        toast({ title: 'Filtè efase', description: 'Tout filtè yo retire', type: 'success' });
    };

    // Fetch monitoring data from API
    const fetchMonitoringData = async (showRefreshIndicator = false, isBackground = false) => {
        if (showRefreshIndicator) setRefreshing(true);
        if (!isBackground && !showRefreshIndicator) setLoading(true);

        try {
            // Prepare agent filter
            const agentFilter = selectedAgent ? [selectedAgent] : undefined;

            // Fetch realtime data (last 60 minutes)
            const realtimeResponse = await monitoringApi.getRealtime(60, agentFilter);

            // Map realtime response to our TransactionItem format for display
            const mappedRealtime: TransactionItem[] = realtimeResponse.recent_transactions.map(tx => ({
                id: tx.id,
                agent: tx.agent_name || 'N/A',
                type: tx.type,
                client: tx.client_name || 'N/A',
                account: tx.compte_code ? `#${tx.compte_code}` : undefined,
                count: tx.count || 1,
                amount: tx.montant,
                time: tx.time || new Date(tx.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
                ago: tx.ago || '',
                subTransactions: tx.sub_transactions?.map(sub => ({
                    id: sub.id,
                    amount: sub.amount,
                    time: sub.time
                }))
            }));

            setRealtimeData(mappedRealtime);

            // Fetch dashboard stats (uses period and agent filters)
            const dashboardResponse = await monitoringApi.getDashboard(period, selectedAgent || undefined);

            // Map dashboard response to stats structure
            const apiByType = (dashboardResponse as any).by_type || dashboardResponse.transactions_by_type || {};
            const depots = (dashboardResponse as any).total_depots || { amount: 0, count: 0 };
            const retraits = (dashboardResponse as any).total_retraits || { amount: 0, count: 0 };
            const balance = (dashboardResponse as any).balance_globale || { montant_net: 0, total_transactions: 0 };

            const mappedStats = {
                by_type: {
                    depo_sol: apiByType.depo_sol || { count: 0, amount: 0, percentage: 0 },
                    depo_bs: apiByType.depo_bs || { count: 0, amount: 0, percentage: 0 },
                    retrait_bs: apiByType.retrait_bs || { count: 0, amount: 0, percentage: 0 },
                    transfer: apiByType.transfer || { count: 0, amount: 0, percentage: 0 },
                    depo_solide: apiByType.depo_solide || { count: 0, amount: 0, percentage: 0 },
                },
                total_depots: {
                    amount: depots.amount || 0,
                    count: depots.count || 0
                },
                total_retraits: {
                    amount: retraits.amount || 0,
                    count: retraits.count || 0
                },
                balance_globale: {
                    montant_net: balance.montant_net || 0,
                    total_transactions: balance.total_transactions || dashboardResponse.total_transactions || 0
                },
                alerts: (dashboardResponse as any).alerts || {},
                // For charts
                daily_trend: (dashboardResponse.daily_trend || []).map((day: any) => ({
                    ...day,
                    name: day.name || new Date(day.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })
                })),
                hourly_distribution: dashboardResponse.hourly_distribution || [],
                total_amount: dashboardResponse.total_amount || 0,
                total_commission: dashboardResponse.total_commission || 0,
                avg_amount: dashboardResponse.avg_amount || 0,
            };

            setStats(mappedStats);

            // Fetch transactions for recent list (paginated, with filters)
            const transactionsResponse = await monitoringApi.getTransactions({
                period: period,
                agent_id: selectedAgent || undefined,
                start_date: startDate || undefined,
                end_date: endDate || undefined,
                page: 1,
                page_size: 5,
                group: true
            });

            // Map transactions response
            const mappedRecent: TransactionItem[] = transactionsResponse.results.transactions.map(tx => ({
                id: tx.id,
                agent: tx.agent_name || 'N/A',
                type: tx.type,
                client: tx.client_name || 'N/A',
                account: tx.compte_code ? `#${tx.compte_code}` : undefined,
                count: tx.group_count || 1,
                amount: parseFloat(tx.formatted_amount?.replace(/[^\d.-]/g, '') || '0'),
                time: new Date(tx.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) + ' ' +
                    new Date(tx.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
            }));

            setRecentData(mappedRecent);
            setLastUpdate(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));

        } catch (error) {
            console.error('Failed to fetch monitoring data:', error);
            // Keep mock data on error
            toast({ title: 'Erè', description: 'Pa kapab chaje done yo. Eseye ankò pita.', type: 'error' });
        } finally {
            setLoading(false);
            if (showRefreshIndicator) setRefreshing(false);
        }
    };

    // Refresh data handler
    const handleRefresh = () => {
        fetchMonitoringData(true);
    };

    // Fetch data when filters change
    useEffect(() => {
        fetchMonitoringData();
    }, [period, selectedAgent, startDate, endDate]);

    // Auto-refresh every 60 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            fetchMonitoringData(false, true);
        }, 60000);
        return () => clearInterval(interval);
    }, [period, selectedAgent]);

    // Export state
    const [exportDate, setExportDate] = useState(new Date().toISOString().split('T')[0]);

    // Handle export
    const handleExport = async () => {
        setExporting(true);
        try {
            await monitoringApi.downloadExcel(exportDate);
            toast({ title: 'Siksè', description: 'Fichye Excel telechaje', type: 'success' });
        } catch (error) {
            toast({ title: 'Erè', description: 'Enposib ekspòte done yo', type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
            <Sidebar />

            <main className="flex-1 p-4 md:p-6 overflow-auto relative">
                {/* Header with gradient */}
                <div className="mb-6">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                <span>Accueil</span>
                                <span>/</span>
                                <span className="text-primary font-medium">Monitoring</span>
                            </div>
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent">
                                Monitoring des Agents
                            </h1>
                            <p className="text-muted-foreground mt-1">
                                Vue d'ensemble des activités et performances en temps réel
                            </p>
                        </div>

                        {/* Quick Stats */}
                        <div className="flex gap-3">
                            <div className="bg-white rounded-xl shadow-sm border px-4 py-3 min-w-[120px]">
                                <p className="text-xs text-muted-foreground">Total Jodi a</p>
                                {loading && !refreshing ? (
                                    <Skeleton className="h-7 w-16 mt-1" />
                                ) : (
                                    <p className="text-xl font-bold text-emerald-600">
                                        <AnimatedCounter value={stats.balance_globale.total_transactions} />
                                    </p>
                                )}
                            </div>
                            <div className="bg-white rounded-xl shadow-sm border px-4 py-3 min-w-[150px]">
                                <p className="text-xs text-muted-foreground">Montan Net</p>
                                {loading && !refreshing ? (
                                    <Skeleton className="h-7 w-24 mt-1" />
                                ) : (
                                    <p className="text-xl font-bold text-blue-600">
                                        {formatCurrency(stats.balance_globale.montant_net, true)}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters Card */}
                <Card className="mb-6 shadow-sm border-0 bg-white/80 backdrop-blur relative z-20">
                    <CardContent className="pt-6">
                        <div className="flex flex-col lg:flex-row gap-4">
                            {/* Agent Search Select */}
                            <div className="flex-1 relative">
                                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Agent</Label>
                                <div className="relative mt-1.5">
                                    <div
                                        className="flex items-center h-11 bg-slate-50 border border-slate-200 rounded-md px-3 cursor-pointer hover:bg-white transition-colors"
                                        onClick={() => setShowAgentDropdown(!showAgentDropdown)}
                                    >
                                        <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" />
                                        <input
                                            type="text"
                                            placeholder="Rechercher un agent..."
                                            value={selectedAgent ? agents.find(a => a.id === selectedAgent)?.name || agentSearchQuery : agentSearchQuery}
                                            onChange={(e) => {
                                                setAgentSearchQuery(e.target.value);
                                                setSelectedAgent('');
                                                setShowAgentDropdown(true);
                                            }}
                                            onFocus={() => setShowAgentDropdown(true)}
                                            className="flex-1 bg-transparent outline-none text-sm"
                                        />
                                        {selectedAgent && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setSelectedAgent(''); setAgentSearchQuery(''); }}
                                                className="p-1 hover:bg-slate-200 rounded"
                                            >
                                                <svg className="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                        <svg className={`h-4 w-4 text-muted-foreground ml-2 transition-transform ${showAgentDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>

                                    {/* Dropdown */}
                                    {showAgentDropdown && (
                                        <div className="absolute z-[999] top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-64 overflow-auto">
                                            {/* All agents option */}
                                            <div
                                                className={`px-4 py-2.5 cursor-pointer hover:bg-slate-50 flex items-center gap-3 ${!selectedAgent ? 'bg-slate-50' : ''}`}
                                                onClick={() => { setSelectedAgent(''); setAgentSearchQuery(''); setShowAgentDropdown(false); }}
                                            >
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center">
                                                    <Users className="h-4 w-4 text-white" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-medium">Tous les agents</p>
                                                    <p className="text-xs text-muted-foreground">Afficher toutes les transactions</p>
                                                </div>
                                            </div>

                                            <div className="border-t border-slate-100"></div>

                                            {/* Agent list */}
                                            {loadingAgents ? (
                                                <div className="px-4 py-6 text-center text-muted-foreground">
                                                    <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" />
                                                    <p className="text-sm">Chaje ajan yo...</p>
                                                </div>
                                            ) : filteredAgents.length > 0 ? (
                                                filteredAgents.map(agent => (
                                                    <div
                                                        key={agent.id}
                                                        className={`px-4 py-2.5 cursor-pointer hover:bg-slate-50 flex items-center gap-3 ${selectedAgent === agent.id ? 'bg-emerald-50' : ''}`}
                                                        onClick={() => { setSelectedAgent(agent.id); setAgentSearchQuery(''); setShowAgentDropdown(false); }}
                                                    >
                                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                                                            <span className="text-white text-xs font-bold">{agent.name.charAt(0)}</span>
                                                        </div>
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium">{agent.name}</p>
                                                            <p className="text-xs text-muted-foreground">@{agent.code}</p>
                                                        </div>
                                                        {selectedAgent === agent.id && (
                                                            <svg className="h-5 w-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="px-4 py-6 text-center text-muted-foreground">
                                                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                    <p className="text-sm">Aucun agent trouvé</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Click outside to close */}
                                {showAgentDropdown && (
                                    <div
                                        className="fixed inset-0 z-40"
                                        onClick={() => setShowAgentDropdown(false)}
                                    />
                                )}
                            </div>

                            {/* Period */}
                            <div className="w-full lg:w-48">
                                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Période</Label>
                                <Select value={period} onValueChange={(v) => setPeriod(v as MonitoringFilters['period'])}>
                                    <SelectTrigger className="h-11 mt-1.5 bg-slate-50 border-slate-200">
                                        <SelectValue placeholder="Sélectionner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PERIOD_OPTIONS.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Custom Date Range */}
                            {period === 'custom' && (
                                <>
                                    <div className="w-full lg:w-40">
                                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Du</Label>
                                        <Input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            className="h-11 mt-1.5 bg-slate-50 border-slate-200"
                                        />
                                    </div>
                                    <div className="w-full lg:w-40">
                                        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Au</Label>
                                        <Input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            className="h-11 mt-1.5 bg-slate-50 border-slate-200"
                                        />
                                    </div>
                                </>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2 lg:self-end">
                                <Button
                                    variant="outline"
                                    onClick={clearFilters}
                                    className="h-11 px-4"
                                >
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                    Nettoyer
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Alerts Section - Only show if there are triggered alerts */}
                {
                    (stats.alerts?.high_activity || stats.alerts?.high_amount) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                            {/* High Activity Alert */}
                            {stats.alerts.high_activity && (
                                <div className="relative overflow-hidden bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200/50 rounded-lg px-4 py-3 group hover:shadow-md transition-shadow">
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-orange-200/30 rounded-full -translate-y-1/2 translate-x-1/2" />
                                    <div className="relative flex items-center gap-3">
                                        <div className="p-2 bg-gradient-to-br from-orange-400 to-amber-500 rounded-lg shadow-md shadow-orange-200">
                                            <Zap className="h-4 w-4 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-orange-700 text-sm">
                                                <span className="font-semibold text-orange-800">Alerte:</span> {stats.alerts.high_activity?.message} - <strong>{stats.alerts.high_activity?.count || 0}</strong> tx
                                            </p>
                                        </div>
                                        <Badge className="bg-orange-500 text-white text-xs px-2 py-0.5">Pik</Badge>
                                    </div>
                                </div>
                            )}

                            {/* High Amount Alert */}
                            {stats.alerts.high_amount && (
                                <div className="relative overflow-hidden bg-gradient-to-r from-red-50 to-rose-50 border border-red-200/50 rounded-lg px-4 py-3 group hover:shadow-md transition-shadow">
                                    <div className="absolute top-0 right-0 w-24 h-24 bg-red-200/30 rounded-full -translate-y-1/2 translate-x-1/2" />
                                    <div className="relative flex items-center gap-3">
                                        <div className="p-2 bg-gradient-to-br from-red-400 to-rose-500 rounded-lg shadow-md shadow-red-200">
                                            <AlertTriangle className="h-4 w-4 text-white" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-red-700 text-sm">
                                                <span className="font-semibold text-red-800">Alerte:</span> {stats.alerts.high_amount?.message} - <strong>{formatCurrency(stats.alerts.high_amount?.amount || 0)}</strong>
                                            </p>
                                        </div>
                                        <Badge className="bg-red-500 text-white text-xs px-2 py-0.5">Kritik</Badge>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                }

                {/* Main Content Grid - Side by Side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Real-time Activity */}
                    <Card className="shadow-sm border-0 bg-white/80 backdrop-blur">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                                        <Activity className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-base">Activité temps réel</CardTitle>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className={`w-2 h-2 rounded-full ${liveIndicator ? 'bg-green-500' : 'bg-green-300'} transition-colors`}></span>
                                            <span className="text-xs text-muted-foreground">LIVE</span>
                                        </div>
                                    </div>
                                </div>
                                <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={refreshing}>
                                    <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                Dernière mise à jour: {lastUpdate}
                            </p>
                        </CardHeader>
                        <CardContent className="max-h-[320px] overflow-y-auto space-y-2">
                            {loading && !refreshing && realtimeData.length === 0 ? (
                                Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="flex items-center gap-3 p-2.5 bg-slate-50/50 rounded-lg">
                                        <Skeleton className="h-10 w-10 rounded-lg" />
                                        <div className="flex-1 space-y-2">
                                            <Skeleton className="h-3 w-24" />
                                            <Skeleton className="h-4 w-32" />
                                        </div>
                                    </div>
                                ))
                            ) : realtimeData.length > 0 ? (
                                realtimeData.map((item, index) => {
                                    const typeConfig = TYPE_CONFIG[item.type] || TYPE_CONFIG['depo_sol'];
                                    const isDeposit = item.type.includes('depo');
                                    return (
                                        <div
                                            key={item.id}
                                            onClick={() => openTransactionDetails(item)}
                                            className="flex items-center gap-3 p-2.5 bg-slate-50/80 rounded-lg hover:bg-slate-100 hover:shadow-sm transition-all cursor-pointer group"
                                        >
                                            {/* Type Icon */}
                                            <div className={`p-2 rounded-lg shrink-0 ${isDeposit ? 'bg-emerald-100' : 'bg-red-100'}`}>
                                                {isDeposit ? (
                                                    <TrendingDown className="h-4 w-4 text-emerald-600" />
                                                ) : (
                                                    <TrendingUp className="h-4 w-4 text-red-600" />
                                                )}
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${isDeposit ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                        {typeConfig.label}
                                                    </span>
                                                    {item.count > 1 && (
                                                        <span className="text-[10px] bg-slate-200 px-1 rounded font-bold">x{item.count}</span>
                                                    )}
                                                </div>
                                                <p className="text-sm font-medium mt-1 truncate">{item.client}</p>
                                            </div>

                                            {/* Amount & Details */}
                                            <div className="text-right shrink-0">
                                                <p className={`font-bold text-sm ${isDeposit ? 'text-emerald-600' : 'text-red-600'}`}>
                                                    {isDeposit ? '+' : '-'}{formatCurrency(item.amount)}
                                                </p>
                                                <p className="text-xs text-muted-foreground">{item.agent} • {item.time}</p>
                                            </div>

                                            {/* Arrow on hover */}
                                            <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="py-12 text-center text-muted-foreground">
                                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-20" />
                                    <p className="text-sm italic">Pa gen okenn aktivite pou kounye a</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Recent Transactions */}
                    <Card className="shadow-sm border-0 bg-white/80 backdrop-blur">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg">
                                        <Clock className="h-5 w-5 text-white" />
                                    </div>
                                    <div>
                                        <CardTitle className="text-base">Transactions récentes</CardTitle>
                                        <p className="text-xs text-muted-foreground">5 dernières transactions</p>
                                    </div>
                                </div>
                                <Button variant="outline" size="sm" onClick={() => navigate('/transactions')}>
                                    Voir tout
                                    <ArrowUpRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="max-h-[320px] overflow-y-auto">
                            <div className="space-y-2">
                                {loading && !refreshing && recentData.length === 0 ? (
                                    Array.from({ length: 5 }).map((_, i) => (
                                        <div key={i} className="flex items-center gap-3 p-3 bg-slate-50/50 rounded-lg">
                                            <Skeleton className="h-10 w-10 rounded-lg" />
                                            <div className="flex-1 space-y-2">
                                                <Skeleton className="h-3 w-28" />
                                                <Skeleton className="h-4 w-40" />
                                            </div>
                                        </div>
                                    ))
                                ) : recentData.length > 0 ? (
                                    recentData.map((item) => {
                                        const typeConfig = TYPE_CONFIG[item.type] || TYPE_CONFIG['depo_sol'];
                                        const isDeposit = item.type.includes('depo');
                                        return (
                                            <div
                                                key={item.id}
                                                onClick={() => openTransactionDetails(item)}
                                                className="flex items-center gap-3 p-3 bg-slate-50/80 rounded-lg hover:bg-slate-100 hover:shadow-sm transition-all cursor-pointer group"
                                            >
                                                {/* Type Icon */}
                                                <div className={`p-2 rounded-lg ${isDeposit ? 'bg-emerald-100' : 'bg-red-100'}`}>
                                                    {isDeposit ? (
                                                        <TrendingDown className={`h-4 w-4 ${isDeposit ? 'text-emerald-600' : 'text-red-600'}`} />
                                                    ) : (
                                                        <TrendingUp className="h-4 w-4 text-red-600" />
                                                    )}
                                                </div>

                                                {/* Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${isDeposit ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                            {typeConfig.label}
                                                        </span>
                                                        {item.count > 1 && (
                                                            <span className="text-xs text-muted-foreground bg-slate-200 px-1.5 py-0.5 rounded">
                                                                ×{item.count}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-sm font-medium mt-1 truncate">{item.client}</p>
                                                </div>

                                                {/* Amount & Details */}
                                                <div className="text-right shrink-0">
                                                    <p className={`font-bold text-sm ${isDeposit ? 'text-emerald-600' : 'text-red-600'}`}>
                                                        {isDeposit ? '+' : '-'}{formatCurrency(item.amount)}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground">{item.agent} • {item.time}</p>
                                                </div>

                                                {/* Arrow on hover */}
                                                <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="py-12 text-center text-muted-foreground">
                                        <History className="h-8 w-8 mx-auto mb-2 opacity-20" />
                                        <p className="text-sm italic">Pa gen okenn tranzaksyon resan</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Statistics Section */}
                <div className="mb-6">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-primary" />
                        Statistiques Globales
                    </h2>

                    {/* Type Distribution */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        {Object.entries(stats.by_type).map(([key, data]) => {
                            const config = TYPE_CONFIG[key] || TYPE_CONFIG['depo_sol'];
                            return (
                                <Card key={key} className="shadow-sm border-0 bg-white/80 backdrop-blur hover:shadow-lg transition-shadow overflow-hidden group">
                                    <div className={`h-1 ${config.bgColor}`}></div>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-medium text-muted-foreground">{config.label}</span>
                                            <Badge variant="outline" className={config.color}>{data.percentage}%</Badge>
                                        </div>
                                        <p className="text-2xl font-bold">{formatCurrency(data.amount, true)}</p>
                                        <div className="flex items-center gap-2 mt-3">
                                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${config.bgColor} rounded-full transition-all duration-1000`}
                                                    style={{ width: `${Math.max(data.percentage, 2)}%` }}
                                                ></div>
                                            </div>
                                            <span className="text-xs font-medium">{formatNumber(data.count)}</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>

                    {/* Totals - Compact */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white/80 backdrop-blur rounded-lg px-4 py-3 border border-slate-100 h-24 flex items-center gap-4 relative overflow-hidden">
                                    <Skeleton className="p-2 w-10 h-10 rounded-lg" />
                                    <div className="flex-1 min-w-0 space-y-2">
                                        <Skeleton className="h-3 w-20" />
                                        <Skeleton className="h-6 w-32" />
                                    </div>
                                    <div className="text-right space-y-2">
                                        <Skeleton className="h-5 w-12 ml-auto" />
                                        <Skeleton className="h-3 w-10 ml-auto" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Total Dépôts */}
                            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg px-4 py-3 flex items-center gap-4 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <TrendingDown className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-emerald-100 text-xs">Total Dépôts</p>
                                    <p className="text-xl font-bold">{formatCurrency(stats.total_depots.amount, true)}</p>
                                </div>
                                <div className="text-right">
                                    <Badge className="bg-white/20 text-white border-0 text-xs">84.7%</Badge>
                                    <p className="text-emerald-100 text-xs mt-1">{formatNumber(stats.total_depots.count)} tx</p>
                                </div>
                            </div>

                            {/* Total Retraits */}
                            <div className="bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg px-4 py-3 flex items-center gap-4 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <TrendingUp className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-red-100 text-xs">Total Retraits</p>
                                    <p className="text-xl font-bold">{formatCurrency(stats.total_retraits.amount, true)}</p>
                                </div>
                                <div className="text-right">
                                    <Badge className="bg-white/20 text-white border-0 text-xs">15.9%</Badge>
                                    <p className="text-red-100 text-xs mt-1">{formatNumber(stats.total_retraits.count)} tx</p>
                                </div>
                            </div>

                            {/* Balance Globale */}
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-lg px-4 py-3 flex items-center gap-4 relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <DollarSign className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-blue-100 text-xs">Balance Globale</p>
                                    <p className="text-xl font-bold">{formatCurrency(stats.balance_globale.montant_net, true)}</p>
                                </div>
                                <div className="text-right">
                                    <Badge className="bg-white/20 text-white border-0 text-xs">Net</Badge>
                                    <p className="text-blue-100 text-xs mt-1">{formatNumber(stats.balance_globale.total_transactions)} tx</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {loading ? (
                        <>
                            <Card className="lg:col-span-2 shadow-sm border-0 bg-white/80 backdrop-blur">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-10 w-10 rounded-lg" />
                                        <Skeleton className="h-6 w-48" />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex gap-4 mb-4">
                                        <Skeleton className="h-4 w-24 rounded-full" />
                                        <Skeleton className="h-4 w-24 rounded-full" />
                                    </div>
                                    <Skeleton className="h-64 w-full rounded-lg" />
                                </CardContent>
                            </Card>
                            <Card className="shadow-sm border-0 bg-white/80 backdrop-blur">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-10 w-10 rounded-lg" />
                                        <Skeleton className="h-6 w-48" />
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex justify-between mb-4">
                                        <Skeleton className="h-8 w-28" />
                                        <Skeleton className="h-8 w-20" />
                                    </div>
                                    <Skeleton className="h-48 w-full rounded-full" />
                                </CardContent>
                            </Card>
                        </>
                    ) : (
                        <>
                            {/* Line Chart */}
                            <Card className="lg:col-span-2 shadow-sm border-0 bg-white/80 backdrop-blur">
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
                                                <BarChart3 className="h-5 w-5 text-white" />
                                            </div>
                                            <CardTitle className="text-base">Évolution des transactions</CardTitle>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button variant="secondary" size="sm" className="text-xs h-8">30J</Button>
                                            <Button variant="ghost" size="sm" className="text-xs h-8">90J</Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {/* Legend */}
                                    <div className="flex flex-wrap gap-4 mb-4 text-xs">
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                                            <span>Depo SOL</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                                            <span>Depo Ti Kanè</span>
                                        </div>
                                    </div>

                                    {/* Real Chart */}
                                    <div className="h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={stats.daily_trend || EMPTY_LINE_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                                                <defs>
                                                    <linearGradient id="colorDepoSol" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                                    </linearGradient>
                                                    <linearGradient id="colorDepoTk" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                                <XAxis
                                                    dataKey="name"
                                                    tick={{ fontSize: 11, fill: '#64748b' }}
                                                    axisLine={{ stroke: '#e2e8f0' }}
                                                    tickLine={false}
                                                />
                                                <YAxis
                                                    tick={{ fontSize: 11, fill: '#64748b' }}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#1e293b',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        color: '#fff',
                                                        fontSize: '12px'
                                                    }}
                                                    formatter={(value: number) => [`${formatCurrency(value)}`, '']}
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="depoSol"
                                                    name="Depo SOL"
                                                    stroke="#10b981"
                                                    strokeWidth={2}
                                                    fillOpacity={1}
                                                    fill="url(#colorDepoSol)"
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="depoTk"
                                                    name="Depo Ti Kanè"
                                                    stroke="#3b82f6"
                                                    strokeWidth={2}
                                                    fillOpacity={1}
                                                    fill="url(#colorDepoTk)"
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Pie Chart */}
                            <Card className="shadow-sm border-0 bg-gradient-to-br from-slate-800 to-slate-900 text-white">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-white/10 rounded-lg">
                                            <PieChart className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-base text-white">Répartition des montants</CardTitle>
                                            <p className="text-xs text-slate-400">Distribution par type</p>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {/* Date & Export */}
                                    <div className="flex items-center justify-between mb-4 gap-2">
                                        <Input
                                            type="date"
                                            value={exportDate}
                                            onChange={(e) => setExportDate(e.target.value)}
                                            className="h-8 w-28 text-xs bg-slate-700/50 border-slate-600 text-white"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="text-xs border-slate-600 text-white hover:bg-slate-700 bg-transparent h-8"
                                            onClick={handleExport}
                                            disabled={exporting}
                                        >
                                            {exporting ? (
                                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                            ) : (
                                                <Download className="h-3 w-3 mr-1" />
                                            )}
                                            Export
                                        </Button>
                                    </div>

                                    {/* Real Pie Chart */}
                                    <div className="h-48">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <RechartsPieChart>
                                                <Pie
                                                    data={Object.entries(stats.by_type).map(([key, data]) => ({
                                                        name: TYPE_CONFIG[key]?.label || key,
                                                        value: data.amount,
                                                        percentage: data.percentage
                                                    }))}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={45}
                                                    outerRadius={70}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                >
                                                    {Object.entries(stats.by_type).map((_, index) => (
                                                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#1e293b',
                                                        border: '1px solid #334155',
                                                        borderRadius: '8px',
                                                        fontSize: '11px'
                                                    }}
                                                    formatter={(value: number) => [formatCurrency(value), 'Montant']}
                                                />
                                            </RechartsPieChart>
                                        </ResponsiveContainer>
                                    </div>

                                    {/* Legend */}
                                    <div className="grid grid-cols-2 gap-2 mt-2">
                                        {Object.entries(stats.by_type).map(([key, data], index) => (
                                            <div key={key} className="flex items-center gap-2">
                                                <div
                                                    className="w-2.5 h-2.5 rounded-full"
                                                    style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                                                ></div>
                                                <span className="text-xs text-slate-300 truncate">
                                                    {TYPE_CONFIG[key]?.label || key} ({data.percentage}%)
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="mt-8 text-center text-xs text-muted-foreground">
                    <p>Dernière synchronisation: {lastUpdate} • Prochaine actualisation dans 60s</p>
                </div>
            </main>

            {/* Transaction Details Modal - Compact Flat UI */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0">
                    {selectedTransaction && (
                        <>
                            {/* Compact Header */}
                            <div className={`px-4 py-3 flex items-center justify-between ${selectedTransaction.type.includes('depo')
                                ? 'bg-emerald-500'
                                : 'bg-red-500'
                                } text-white`}>
                                <div className="flex items-center gap-2">
                                    {selectedTransaction.type.includes('depo') ? (
                                        <TrendingDown className="h-4 w-4" />
                                    ) : (
                                        <TrendingUp className="h-4 w-4" />
                                    )}
                                    <span className="font-medium text-sm">
                                        {TYPE_CONFIG[selectedTransaction.type]?.label}
                                    </span>
                                    {selectedTransaction.count > 1 && (
                                        <span className="bg-white/20 text-xs px-1.5 py-0.5 rounded">
                                            {selectedTransaction.count}x
                                        </span>
                                    )}
                                </div>
                                <span className="font-bold">
                                    {selectedTransaction.type.includes('depo') ? '+' : '-'}
                                    {formatCurrency(selectedTransaction.amount)}
                                </span>
                            </div>

                            {/* Content - Compact */}
                            <div className="p-4 space-y-3">
                                {/* Client Row */}
                                <div className="flex items-center gap-3">
                                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white font-medium text-sm ${selectedTransaction.type.includes('depo') ? 'bg-emerald-500' : 'bg-red-500'
                                        }`}>
                                        {selectedTransaction.client.charAt(0)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">{selectedTransaction.client}</p>
                                        <p className="text-xs text-muted-foreground">{selectedTransaction.account || 'Client'}</p>
                                    </div>
                                </div>

                                {/* Info Row - Inline */}
                                <div className="flex items-center gap-4 text-sm py-2 border-y border-slate-100">
                                    <div className="flex items-center gap-1.5 text-muted-foreground">
                                        <Users className="h-3.5 w-3.5" />
                                        <span>{selectedTransaction.agent}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-muted-foreground">
                                        <Clock className="h-3.5 w-3.5" />
                                        <span>{selectedTransaction.time}</span>
                                        {selectedTransaction.ago && <span className="text-xs">({selectedTransaction.ago})</span>}
                                    </div>
                                </div>

                                {/* Sub-transactions - Compact List */}
                                {selectedTransaction.subTransactions && selectedTransaction.subTransactions.length > 0 && (
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                                Détay ({selectedTransaction.subTransactions.length})
                                            </span>
                                        </div>
                                        <div className="space-y-1 max-h-40 overflow-y-auto">
                                            {selectedTransaction.subTransactions.map((sub, index) => (
                                                <div
                                                    key={sub.id}
                                                    className="flex items-center justify-between py-1.5 px-2 bg-slate-50 rounded text-sm"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${selectedTransaction.type.includes('depo')
                                                            ? 'bg-emerald-100 text-emerald-600'
                                                            : 'bg-red-100 text-red-600'
                                                            }`}>
                                                            {index + 1}
                                                        </span>
                                                        <span className="text-muted-foreground text-xs">{sub.time}</span>
                                                    </div>
                                                    <span className={`font-medium ${selectedTransaction.type.includes('depo') ? 'text-emerald-600' : 'text-red-600'
                                                        }`}>
                                                        {formatCurrency(sub.amount)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        {/* Total */}
                                        <div className={`flex items-center justify-between mt-2 pt-2 border-t text-sm ${selectedTransaction.type.includes('depo') ? 'border-emerald-200' : 'border-red-200'
                                            }`}>
                                            <span className="font-medium">Total</span>
                                            <span className={`font-bold ${selectedTransaction.type.includes('depo') ? 'text-emerald-600' : 'text-red-600'
                                                }`}>
                                                {formatCurrency(selectedTransaction.amount)}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                {/* Action Button - Single */}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full"
                                    onClick={() => setShowDetailModal(false)}
                                >
                                    Fermer
                                </Button>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
